import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { submitReviewDecision } from '../../services/api'

interface ReviewFormProps {
  reviewId: string
  onSubmitted: () => void
}

export default function ReviewForm({ reviewId, onSubmitted }: ReviewFormProps) {
  const queryClient = useQueryClient()
  const [decision, setDecision] = useState<'approved' | 'rejected' | 'changes_requested'>('approved')
  const [feedback, setFeedback] = useState('')

  const [submitError, setSubmitError] = useState<string | null>(null)

  const submitMutation = useMutation({
    mutationFn: () => submitReviewDecision(reviewId, { decision, feedback }),
    onMutate: () => setSubmitError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      onSubmitted()
    },
    // BUG-121: surface a failed submission instead of silently swallowing it —
    // the reviewer previously got no feedback and assumed their decision landed.
    onError: () => setSubmitError('Could not submit your review. Please try again.'),
  })

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Decision</label>
        <div className="flex gap-2">
          {(['approved', 'rejected', 'changes_requested'] as const).map((d) => (
            <button
              key={d}
              onClick={() => setDecision(d)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                decision === d
                  ? d === 'approved' ? 'bg-green-50 border-green-300 text-green-700'
                    : d === 'rejected' ? 'bg-red-50 border-red-300 text-red-700'
                    : 'bg-yellow-50 border-yellow-300 text-yellow-700'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              {d === 'changes_requested' ? 'Changes Requested' : d.charAt(0).toUpperCase() + d.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Feedback</label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 light-input text-sm outline-none resize-none"
          placeholder="Add your review feedback..."
        />
      </div>

      {submitError && (
        <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          {submitError}
        </p>
      )}

      <button
        onClick={() => submitMutation.mutate()}
        disabled={submitMutation.isPending}
        className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
      >
        {submitMutation.isPending ? 'Submitting...' : 'Submit Review'}
      </button>
    </div>
  )
}
